/**
 * ⚠️ DEPRECATED FOR BETA — NOT THE CANONICAL INVENTORY SOURCE
 * ────────────────────────────────────────────────────────────
 * As of Phase 1 of the Inventory Integration Plan (2026-04-28),
 * `WarehouseInventoryContext` is the canonical owner of physical stock.
 *
 * This context wraps the simulated BoxHero adapter and uses an
 * unrelated SKU namespace (`BASE-SEN-3060`, etc.). It is retained as a
 * future integration boundary for live BoxHero connectivity but MUST NOT
 * be treated as source of truth.
 *
 * Rules:
 *   • Do NOT add new consumers of `useInventory()`.
 *   • Existing consumers are advisory only and will be migrated in later phases.
 *   • For real stock decisions, use `useWarehouseInventory()`.
 *
 * See: src/data/inventory/CANONICAL.md
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import {
  getInventoryAdapter,
  getInventoryRecord,
  type InventoryAdapter,
  type InventorySnapshot,
  type StockLevel,
  type StockStatus,
} from "./inventoryAdapter";
import { fetchJobMaterials, type JobWithMaterials } from "@/services/inventory.service";

interface InventoryContextValue {
  snapshot: InventorySnapshot;
  adapter: InventoryAdapter;
  /** Convenience: Record<sku, availableQty> for SKU mapping */
  inventoryRecord: Record<string, number>;
  /** Get stock level for a single SKU */
  getStock: (sku: string) => StockLevel | undefined;
  /** Check if a specific quantity is available */
  checkAvailability: (sku: string, qty: number) => {
    available: boolean;
    quantityAvailable: number;
    shortage: number;
    status: StockStatus;
  };
  /** Whether connected to BoxHero */
  isConnected: boolean;
  /** Force refresh snapshot */
  refresh: () => void;
  /** Loading state */
  isLoading: boolean;
  /** Job materials from Supabase */
  jobMaterials: JobWithMaterials[];
}

const InventoryContext = createContext<InventoryContextValue | null>(null);

export function InventoryProvider({ children }: { children: ReactNode }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [snapshot, setSnapshot] = useState<InventorySnapshot>({
    items: [],
    lastRefreshedAt: new Date().toISOString(),
    source: "simulated",
    connected: false,
  });
  const [inventoryRecord, setInventoryRecord] = useState<Record<string, number>>({});
  const [jobMaterials, setJobMaterials] = useState<JobWithMaterials[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  const adapter = getInventoryAdapter();

  useEffect(() => {
    if (deferHeavyProvider) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    async function loadInventory() {
      setIsLoading(true);
      try {
        const [snap, record, materials] = await Promise.all([
          adapter.getSnapshot(),
          getInventoryRecord(),
          fetchJobMaterials(),
        ]);
        if (mounted) {
          setSnapshot(snap);
          setInventoryRecord(record);
          setJobMaterials(materials);
        }
      } catch (error) {
        console.error("Failed to load inventory:", error);
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    loadInventory();

    return () => {
      mounted = false;
    };
  }, [adapter, deferHeavyProvider, refreshKey]);

  const getStock = useCallback((sku: string): StockLevel | undefined => {
    return snapshot.items.find((i) => i.sku === sku);
  }, [snapshot.items]);

  const checkAvailability = useCallback((sku: string, qty: number) => {
    const item = snapshot.items.find((i) => i.sku === sku);
    const available = item?.quantityAvailable ?? 0;
    return {
      available: available >= qty,
      quantityAvailable: available,
      shortage: Math.max(0, qty - available),
      status: item?.status ?? ("Out of Stock" as StockStatus),
    };
  }, [snapshot.items]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <InventoryContext.Provider
      value={{
        snapshot,
        adapter,
        inventoryRecord,
        getStock,
        checkAvailability,
        isConnected: adapter.isConnected(),
        refresh,
        isLoading,
        jobMaterials,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory() {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error("useInventory must be used within InventoryProvider");
  if (import.meta.env.DEV) {
    // One-time dev signal: this hook is deprecated as a source of truth.
    // See src/data/inventory/CANONICAL.md
    // Use useWarehouseInventory() for canonical stock.
    // (Console.debug to avoid noise in normal dev flows.)
    console.debug(
      "[inventory] useInventory() is deprecated for canonical stock — use useWarehouseInventory()."
    );
  }
  return ctx;
}
